import { Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { CopySimple, Envelope } from "@phosphor-icons/react"
import { t } from "i18next"
import { Card, CardBody, CardHeader, CardHeaderTitle } from "@/components/ui/card.tsx"
import { List, ListItem, ListText } from "@/components/ui/list"
import { toast } from "@/hooks/useToast"
import { getCleanPhoneNumber } from "@/lib/utils"
import { ChDataGuardPresenceTag } from "@/modules/patients/information/consent/ch/ch-data-guard-presence-tag"
import { useChSendDataGuardPatientConsentMutation } from "@/modules/patients/information/consent/ch/services/useChSendDataGuardPatientConsentMutation"
import { ConsentProps } from "@/modules/patients/information/consent/consent"
import { useConfirmStore } from "@/stores/useConfirmStore"

const ChConsent = ({
  contactChannel,
  phoneNumber,
  patientId,
  dataGuardPatientIdentifier,
  isEditMode,
}: ConsentProps) => {
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)
  const [chSendDataGuardPatientConsent] = useChSendDataGuardPatientConsentMutation()

  const handleSendLinkToPatient = () => {
    const formattedPhoneNumber = getCleanPhoneNumber(phoneNumber)
    confirm({
      title: t("patient.information.consent.sendLinkToPatient"),
      cancelText: t("common.button.cancel"),
      confirmText: t("patient.information.consent.sendLinkButton"),
      content: <p>{t("patient.information.consent.sendLinkDialogContent", { phoneNumber: formattedPhoneNumber })}</p>,
      onConfirm() {
        chSendDataGuardPatientConsent({
          variables: {
            input: {
              patientId,
            },
          },
          onCompleted(response) {
            if (response.ch_sendDataGuardPatientConsent.boolean)
              toast({
                description: t("patient.information.consent.messages.sendLinkSuccessfully", {
                  phoneNumber: formattedPhoneNumber,
                }),
              })
            else
              toast({
                description: t("patient.information.consent.messages.sendLinkFailed", {
                  phoneNumber: formattedPhoneNumber,
                }),
              })
          },
          onError() {
            toast({
              description: t("patient.information.consent.messages.sendLinkFailed", {
                phoneNumber: formattedPhoneNumber,
              }),
            })
          },
        })
        cancel()
      },
      onCancel() {
        cancel()
      },
    })
  }

  const handleCopy = () => {
    navigator.clipboard
      .writeText(dataGuardPatientIdentifier)
      .then(() =>
        toast({
          description: t("patient.information.consent.messages.copySuccessfully"),
        }),
      )
      .catch(() =>
        toast({
          description: t("patient.information.consent.messages.copyFailed"),
        }),
      )
  }

  return (
    <Card className="animate-fade-in-top w-full">
      <CardHeader>
        <div className="flex items-center gap-4">
          <CardHeaderTitle data-testid="card-header-title-content">
            {t("patient.information.consent.title")}
          </CardHeaderTitle>
          <ErrorBoundary fallback={null}>
            <Suspense fallback={null}>
              <ChDataGuardPresenceTag patientId={patientId} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </CardHeader>
      <CardBody>
        <List className="px-4 pb-4">
          <ListItem asChild data-testid="data-guard-patient-id-copy-item" className="hover:enabled:bg-surface-hovered">
            <button>
              <div className="flex items-center gap-2">
                <CopySimple className="text-on-surface-subdued h-6 w-6 shrink-0" />
                <div className="grid min-w-0">
                  <ListText className="truncate" onClick={handleCopy}>
                    {t("patient.information.consent.copyDataGuardPatientId")}
                  </ListText>
                </div>
              </div>
            </button>
          </ListItem>
          <ListItem asChild data-testid="patient-link-send-item" className="hover:enabled:bg-surface-hovered">
            <button disabled={contactChannel !== "EMAIL_AND_SMS" || !isEditMode}>
              <div className="flex items-center gap-2">
                <Envelope className="text-on-surface-subdued h-6 w-6 shrink-0" />
                <div className="grid min-w-0">
                  <ListText
                    className="truncate"
                    title={
                      contactChannel !== "EMAIL_AND_SMS"
                        ? t(`patient.information.consent.tooltip.${contactChannel}`)
                        : ""
                    }
                    onClick={handleSendLinkToPatient}>
                    {t("patient.information.consent.sendLinkToPatient")}
                  </ListText>
                </div>
              </div>
            </button>
          </ListItem>
        </List>
      </CardBody>
    </Card>
  )
}
export { ChConsent }
